'use client';
import React, { useEffect, useRef } from 'react';

const AUDIENCES = [
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    ),
    title: 'Business Leaders',
    desc: 'CEOs, directors, and executives who need to make confident strategic decisions with clarity on competitive positioning.',
    tags: ['Strategy', 'Competitive Analysis'],
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="14" rx="2"/>
        <path d="M8 21h8"/>
        <path d="M12 17v4"/>
      </svg>
    ),
    title: 'Managers & Teams',
    desc: 'Department heads and team leads running quarterly planning, product reviews, or initiative assessments.',
    tags: ['Planning', 'Team Alignment'],
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
      </svg>
    ),
    title: 'Entrepreneurs',
    desc: 'Founders and startup builders validating new ideas, entering markets, or reassessing their competitive moat.',
    tags: ['Startups', 'Market Entry'],
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>
        <polyline points="14 2 14 8 20 8"/>
        <line x1="16" y1="13" x2="8" y2="13"/>
        <line x1="16" y1="17" x2="8" y2="17"/>
        <line x1="10" y1="9" x2="8" y2="9"/>
      </svg>
    ),
    title: 'Project Teams',
    desc: 'Cross-functional teams launching new initiatives, managing complex projects, or preparing for stakeholder reviews.',
    tags: ['Project Management', 'Execution'],
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
      </svg>
    ),
    title: 'Consultants & Advisors',
    desc: 'Strategy consultants, business coaches, and advisors who facilitate SWOT workshops and need a reliable, repeatable framework.',
    tags: ['Consulting', 'Facilitation'],
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
        <path d="M6 12v5c3 3 9 3 12 0v-5"/>
      </svg>
    ),
    title: 'Students & Academics',
    desc: 'MBA students, business school participants, and academic researchers studying strategic management frameworks.',
    tags: ['MBA', 'Academic Research'],
  },
];

export default function AudienceSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('is-visible');
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    const el = sectionRef?.current;
    if (el) {
      el?.querySelectorAll('.reveal-up, .reveal-blur, .stagger-grid')?.forEach((child) => observer?.observe(child));
      observer?.observe(el);
    }
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="audience"
      className="relative w-full py-28 px-6 lg:px-12 border-t border-white/5 overflow-hidden"
      style={{ background: 'var(--surface)' }}
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse 50% 60% at 80% 50%, rgba(26,43,74,0.3) 0%, transparent 70%)' }}
      />
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6">
          <div>
            <span className="font-mono text-xs text-accent uppercase tracking-widest reveal-up block mb-4">Who It&apos;s For</span>
            <h2 className="font-manrope text-5xl lg:text-7xl font-semibold tracking-tight leading-[0.9]">
              <span className="block reveal-text-line">Built for</span>
              <span className="block text-foreground/30 reveal-text-line" style={{ transitionDelay: '0.1s' }}>decision</span>
              <span className="block reveal-text-line" style={{ transitionDelay: '0.2s' }}>makers.</span>
            </h2>
          </div>
          <p className="reveal-blur font-mono text-foreground/35 text-sm max-w-xs text-right">
            // LEADERS // TEAMS // FOUNDERS
          </p>
        </div>

        {/* Audience grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 border border-white/5 rounded-2xl overflow-hidden stagger-grid">
          {AUDIENCES?.map((audience) => (
            <div
              key={audience?.title}
              className="group relative p-7 flex flex-col gap-4 cursor-default hover:bg-accent/[0.04] transition-colors duration-500 hover-card-shine"
              style={{ background: 'var(--background)' }}
            >
              {/* Top accent line on hover */}
              <div
                className="absolute top-0 left-0 right-0 h-px opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{ background: 'linear-gradient(to right, transparent, rgba(201,168,76,0.4), transparent)' }}
              />

              {/* Icon */}
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center text-foreground/50 group-hover:text-accent transition-colors duration-300"
                style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}
              >
                {audience?.icon}
              </div>

              <div className="flex flex-col gap-2">
                <h3 className="font-manrope font-semibold text-base text-foreground tracking-tight group-hover:text-accent transition-colors duration-300">
                  {audience?.title}
                </h3>
                <p className="text-foreground/45 text-sm leading-relaxed font-light">
                  {audience?.desc}
                </p>
              </div>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mt-auto">
                {audience?.tags?.map((tag) => (
                  <span
                    key={tag}
                    className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-full text-foreground/35"
                    style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}
                  >
                    {tag}
                  </span>
                ))}
              </div>

              {/* Bottom accent line */}
              <div
                className="absolute bottom-0 left-0 h-px w-0 group-hover:w-full transition-all duration-500"
                style={{ background: 'linear-gradient(to right, rgba(201,168,76,0.5), transparent)' }}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}